import type { Router } from 'vue-router';
import {
  toRef,
  watch,
} from 'vue';

import { AccessDeniedError } from '@modules/core/errors';
import { ADMIN_DASHBOARD_ROUTE_NAME, AFFILIATE_DASHBOARD_ROUTE_NAME } from '@modules/dashboard/constants';
import { LOGIN_ROUTE_NAME } from '@modules/login/constants';
import { useCurrentNamespace } from '@modules/permissions/composables';
import { usePermissionsStore } from '@modules/permissions/store';

export default function createPermissionGuard(router: Router): void {
  const permissionsStore = usePermissionsStore();
  const { hasPermission } = permissionsStore;
  const permissions = toRef(permissionsStore, 'permissions');

  async function waitPermissionsLoaded(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (permissions.value) {
        resolve();
        return;
      }

      const unwatchPermissions = watch(permissions, () => {
        unwatchPermissions();
        resolve();
      });
    });
  }

  router.beforeEach(async (to) => {
    await waitPermissionsLoaded();

    const permission = to.meta?.permission;

    if (permission) {
      if (hasPermission(permission).value)
        return true;

      return new AccessDeniedError();
    }

    return true;
  });

  router.onError((error) => {
    if (error instanceof AccessDeniedError) {
      redirect(router);
    }
  });
}

function redirect(router: Router): void {
  const { isAdminNamespace, isAffiliateNamespace } = useCurrentNamespace();
  if (isAdminNamespace.value) {
    void router.push({
      name: ADMIN_DASHBOARD_ROUTE_NAME,
    });
  } else if (isAffiliateNamespace.value) {
    void router.push({
      name: AFFILIATE_DASHBOARD_ROUTE_NAME,
    });
  } else {
    void router.push({
      name: LOGIN_ROUTE_NAME,
    });
  }
}
